/**
 * Parses WhatsApp-style markup into safe React-friendly segments.
 * Does not emit raw HTML strings.
 */

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/g;
const FORMAT_RULES = [
  { pattern: /\*(.*?)\*/g, tag: 'bold' },
  { pattern: /_(.*?)_/g, tag: 'italic' },
  { pattern: /~(.*?)~/g, tag: 'strike' },
  { pattern: /```(.*?)```/g, tag: 'code' },
];

function isSafeUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function splitByUrls(text) {
  const parts = [];
  let lastIndex = 0;
  let match = URL_REGEX.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, match.index) });
    }
    parts.push({ type: 'link', value: match[1] });
    lastIndex = match.index + match[0].length;
    match = URL_REGEX.exec(text);
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) });
  }

  URL_REGEX.lastIndex = 0;
  return parts.length ? parts : [{ type: 'text', value: text }];
}

function applyInlineFormatting(text) {
  for (const rule of FORMAT_RULES) {
    const next = [];
    for (const segment of text) {
      if (segment.type !== 'text') {
        next.push(segment);
        continue;
      }

      let cursor = 0;
      let match = rule.pattern.exec(segment.value);
      while (match) {
        if (match.index > cursor) {
          next.push({ type: 'text', value: segment.value.slice(cursor, match.index) });
        }
        next.push({ type: rule.tag, value: match[1] });
        cursor = match.index + match[0].length;
        match = rule.pattern.exec(segment.value);
      }
      rule.pattern.lastIndex = 0;

      if (cursor < segment.value.length) {
        next.push({ type: 'text', value: segment.value.slice(cursor) });
      }
    }
    text = next.length ? next : text;
  }
  return text;
}

export function parseWhatsAppText(text) {
  if (typeof text !== 'string' || !text) return [];

  const lines = text.split('\n');
  const segments = [];

  lines.forEach((line, lineIndex) => {
    const urlParts = splitByUrls(line);
    urlParts.forEach((part) => {
      if (part.type === 'link' && isSafeUrl(part.value)) {
        segments.push({ type: 'link', value: part.value });
        return;
      }
      if (part.type === 'text' && part.value) {
        segments.push(...applyInlineFormatting([part]));
      }
    });
    if (lineIndex < lines.length - 1) {
      segments.push({ type: 'break' });
    }
  });

  return segments;
}

/** @deprecated Use parseWhatsAppText + React rendering instead. */
export function formatWhatsAppText(text) {
  return parseWhatsAppText(text)
    .map((segment) => {
      if (segment.type === 'break') return '\n';
      if (segment.type === 'link') return segment.value;
      return segment.value || '';
    })
    .join('');
}
